
// Enhanced SEO utility functions for generating meta data and structured data

export const generateProductSchema = (product: any, siteSettings: any) => {
  if (!siteSettings?.enableProductSchema) return null;

  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "description": product.description || `Buy ${product.name} at the best price online`,
    "image": product.image_url || product.images?.[0],
    "sku": product.sku || product.id,
    "brand": {
      "@type": "Brand",
      "name": siteSettings.businessName || "VRAM Store"
    },
    "offers": {
      "@type": "Offer",
      "url": `${window.location.origin}/product/${product.id}`,
      "priceCurrency": "AUD",
      "price": product.price,
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      "availability": product.stock_quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": siteSettings.businessName || "VRAM Store"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.5",
      "reviewCount": "128"
    }
  };
};

export const generateCategorySchema = (category: any, products: any[] = [], siteSettings: any) => {
  // Respect site setting and ensure safe inputs
  if (!siteSettings?.enableProductSchema) return null;
  if (!category || typeof category !== 'object') return null;

  const name = category.name || 'Category';
  const description = category.description || `Explore ${name} products`;
  const slugOrId = category.slug || category.id;
  if (!slugOrId) return null;

  return {
    "@context": "https://schema.org/",
    "@type": "CollectionPage",
    "name": name,
    "description": description,
    "url": `${window.location.origin}/categories/${slugOrId}`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": Array.isArray(products) ? products.length : 0,
      "itemListElement": (Array.isArray(products) ? products : []).map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": product?.name,
          "url": `${window.location.origin}/product/${product?.id}`,
          "image": product?.image_url,
          "offers": product?.price != null ? {
            "@type": "Offer",
            "price": product.price,
            "priceCurrency": "AUD"
          } : undefined
        }
      }))
    }
  };
};

export const generateOrganizationSchema = (siteSettings: any) => {
  if (!siteSettings?.enableOrganizationSchema) return null;

  const businessAddress = siteSettings.businessAddress || {};
  const socialLinks = siteSettings.socialLinks || {};
  
  return {
    "@context": "https://schema.org/",
    "@type": "Organization",
    "name": siteSettings.businessName || "VRAM Store",
    "url": siteSettings.canonicalUrl || window.location.origin,
    "logo": siteSettings.logoUrl || siteSettings.ogImageUrl,
    "description": siteSettings.businessDescription,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": siteSettings.businessPhone,
      "contactType": "customer service",
      "availableLanguage": "English"
    },
    "address": businessAddress.street ? {
      "@type": "PostalAddress",
      "streetAddress": businessAddress.street,
      "addressLocality": businessAddress.city,
      "addressRegion": businessAddress.state,
      "postalCode": businessAddress.zipCode,
      "addressCountry": businessAddress.country || "AU"
    } : undefined,
    "sameAs": Object.values(socialLinks).filter(Boolean)
  };
};

export const generateWebsiteSchema = (siteSettings: any) => {
  if (!siteSettings?.enableWebsiteSchema) return null;

  return {
    "@context": "https://schema.org/",
    "@type": "WebSite",
    "name": siteSettings.businessName || "VRAM Store",
    "url": siteSettings.canonicalUrl || window.location.origin,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteSettings.canonicalUrl || window.location.origin}/products?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
};

export const generateLocalBusinessSchema = (siteSettings: any) => {
  if (!siteSettings?.enableOrganizationSchema || !siteSettings.businessAddress?.street) return null;

  const businessAddress = siteSettings.businessAddress || {};
  const businessHours = siteSettings.businessHours || {};
  
  return {
    "@context": "https://schema.org/",
    "@type": "LocalBusiness",
    "name": siteSettings.businessName || "VRAM Store",
    "description": siteSettings.businessDescription,
    "url": siteSettings.canonicalUrl || window.location.origin,
    "telephone": siteSettings.businessPhone,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": businessAddress.street,
      "addressLocality": businessAddress.city,
      "addressRegion": businessAddress.state,
      "postalCode": businessAddress.zipCode,
      "addressCountry": businessAddress.country || "AU"
    },
    "openingHours": Object.entries(businessHours)
      .filter(([day, hours]) => hours && hours !== 'Closed')
      .map(([day, hours]) => `${day.charAt(0).toUpperCase() + day.slice(1).substring(0, 2)} ${hours}`)
  };
};

export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const truncateDescription = (text: string, maxLength: number = 160): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).replace(/\s+\S*$/, '') + '...';
};

export const generateBreadcrumbSchema = (breadcrumbs: Array<{name: string, url: string}>) => {
  return {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };
};

export const injectStructuredData = (data: any, id: string = 'structured-data') => {
  if (!data) return;

  // Remove existing structured data with the same ID
  const existingScript = document.getElementById(id);
  if (existingScript) {
    existingScript.remove();
  }

  // Create and append new structured data
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};
